import Heading from '@app-studio-web/ui/components/Heading'
import { AnswerType } from '@app-studio-web/ui/types'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'
import QuizLayout from './QuizLayout'

const LETTERS = 'ABCDEFGHIJ'

// Quiz-specific single-choice page. Mirrors the shared SingleChoiceQuestionPage
// submit mechanics (Form method=post + useSubmit, hidden radio whose name is the
// English title and whose value is the answer value; auto-advance on change),
// but renders on the quiz layout (progress header) and adds JumpSpeak-style
// letter badges. Set `showLetters={false}` to render an emoji badge instead
// (used by the language step so the flag list stays intact).
export default function QuizChoicePage({
  answers,
  title,
  subtitle,
  onSubmit = () => {},
  preselectedValue,
  translationInterpolation,
  showLetters = true,
}: {
  answers: AnswerType[]
  title: string
  subtitle?: string
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void
  preselectedValue?: string
  /** Passed to `t()` for title/subtitle/answers (e.g. `{{language}}`) */
  translationInterpolation?: Record<string, string>
  showLetters?: boolean
}) {
  const submit = useSubmit()
  const { t } = useTranslation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    onSubmit(event as unknown as React.FormEvent<HTMLFormElement>)
    submit(event.currentTarget.form)
  }

  const questionName = t(title, { lng: 'en', ...translationInterpolation })

  return (
    <QuizLayout>
      <div className="shrink-0">
        <Heading
          subtitle={
            subtitle ? t(subtitle, translationInterpolation) : undefined
          }
          title={t(title, translationInterpolation)}
        />
      </div>

      <div className="flex grow flex-col justify-center">
        <Form className="space-y-4" method="post">
          {answers.map((answer, index) => {
            const value =
              answer.value ??
              t(answer.title, { lng: 'en', ...translationInterpolation })
            const checked = preselectedValue
              ? preselectedValue === answer.value
              : undefined

            return (
              <label
                key={t(answer.title, {
                  lng: 'en',
                  ...translationInterpolation,
                })}
                className="has-[:checked]:border-primary flex w-full cursor-pointer items-center gap-3 rounded-xl border-2 border-[#EAEAEA] bg-white p-4 transition-all hover:scale-102 hover:opacity-80 hover:drop-shadow-lg"
              >
                <input
                  className="hidden"
                  defaultChecked={checked}
                  name={questionName}
                  onChange={handleChange}
                  type="radio"
                  value={value}
                />

                <span
                  className={
                    showLetters
                      ? 'text-primary bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold'
                      : 'rounded-lg bg-transparent px-2 py-1 text-xl'
                  }
                >
                  {showLetters ? LETTERS[index] : answer.emoji}
                </span>

                <div className="flex flex-col">
                  <span className="text-custom-black font-headline font-bold">
                    {t(answer.title, translationInterpolation)}
                  </span>
                  {answer.subtitle && (
                    <span className="text-sm text-[#757575]">
                      {t(answer.subtitle, translationInterpolation)}
                    </span>
                  )}
                </div>
              </label>
            )
          })}
        </Form>
      </div>
    </QuizLayout>
  )
}
